import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  PageHeader, DashboardCard, StatusBadge, Button,
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@zafroi/ui-kit';
import { useQuote, useQuoteTransition } from '@/hooks/useCommercial';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { quoteStatusVariants } from '@/config/commercial-status';
import { formatMoney, shortDate } from '@/lib/format';
import type { KoshQuoteStatus } from '@/types/commercial-docs';

const ACTIONS: Partial<Record<KoshQuoteStatus, { to: KoshQuoteStatus; label: string }[]>> = {
  DRAFT: [{ to: 'SENT', label: 'Send' }, { to: 'REJECTED', label: 'Reject' }],
  SENT: [{ to: 'ACCEPTED', label: 'Mark accepted' }, { to: 'REJECTED', label: 'Reject' }],
  ACCEPTED: [{ to: 'CONVERTED', label: 'Convert' }],
};

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuote(id);
  const nameMap = useCustomerNameMap();
  const transition = useQuoteTransition();

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(x) => {
          const actions = ACTIONS[x.status] ?? [];
          const lines = x.lineItems ?? [];
          return (
            <>
              <PageHeader
                eyebrow={<Link to="/quotes" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Quotes</Link>}
                title={x.quoteNumber}
                meta={<StatusBadge status={x.status} variants={quoteStatusVariants} />}
                actions={
                  <div className="flex gap-2">
                    {actions.map((a, i) => (
                      <Button key={a.to} size="sm" variant={i === 0 ? 'default' : 'outline'}
                        disabled={transition.isPending} onClick={() => id && transition.mutate({ id, to: a.to })}>
                        {a.label}
                      </Button>
                    ))}
                  </div>
                }
              />
              <DashboardCard title="Summary">
                <div className="space-y-1.5 text-[13px]">
                  <Row k="Customer" v={nameMap.get(x.customerId) ?? x.customerId.slice(0, 8)} />
                  <Row k="Valid until" v={x.validUntil ? shortDate(x.validUntil) : '—'} />
                  <Row k="Sub-total" v={formatMoney(x.subTotal, x.currency)} />
                  <div className="flex justify-between border-t border-border pt-1.5 font-bold">
                    <span>Total</span><span className="tabular-nums">{formatMoney(x.total, x.currency)}</span>
                  </div>
                  {x.notes && <Row k="Notes" v={x.notes} />}
                </div>
              </DashboardCard>
              <DashboardCard title="Line items" flushBody>
                {lines.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No line items.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Amount</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{l.description}</TableCell>
                          <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(l.unitPrice, x.currency)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(l.amount ?? l.unitPrice * l.quantity, x.currency)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DashboardCard>
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>;
}
